import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import type { Tables } from "@/integrations/supabase/types";

type Customer = Tables<"customers">;

interface CustomerWithStats extends Customer {
  totalAppointments: number;
  lastVisit: string | null;
}

export function useCustomers() {
  const { business } = useBusiness();
  const [customers, setCustomers] = useState<CustomerWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statLabel, setStatLabel] = useState<"appointments" | "visits" | "orders">("appointments");

  const fetchCustomers = useCallback(async () => {
    if (!business) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch customers
      const { data: customersData, error: customersError } = await supabase
        .from("customers")
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });

      if (customersError) throw customersError;

      const primaryModule = (business as any)?.primary_module || "appointments";

      // Membership-first businesses: show Visits (from Access Desk check-ins/outs)
      if (primaryModule === "memberships") {
        setStatLabel("visits");

        const { data: sessions, error: sessionsError } = await supabase
          // types.ts isn't regenerated yet for new module tables
          .from("access_sessions" as any)
          .select("customer_id, check_in_at")
          .eq("business_id", business.id);

        if (sessionsError) throw sessionsError;

        const stats = new Map<string, { count: number; last: string | null }>();
        for (const s of sessions || []) {
          const cid = (s as any).customer_id as string;
          const at = (s as any).check_in_at as string;
          const prev = stats.get(cid) || { count: 0, last: null };
          const last =
            !prev.last || new Date(at).getTime() > new Date(prev.last).getTime() ? at : prev.last;
          stats.set(cid, { count: prev.count + 1, last });
        }

        const customersWithStats = (customersData || []).map((customer) => {
          const st = stats.get(customer.id);
          return {
            ...customer,
            totalAppointments: st?.count || 0, // reused field name to avoid breaking UI code
            lastVisit: st?.last || null,
          };
        });

        setCustomers(customersWithStats);
        return;
      }

      // Catering services: show Orders (from cart-based food orders)
      if (primaryModule === "catering_services" || primaryModule === "food_vendors") {
        setStatLabel("orders");

        const { data: foodOrders, error: ordersErr } = await supabase
          .from("food_orders")
          .select("id, created_at, customer_id")
          .eq("business_id", business.id);
        if (ordersErr) throw ordersErr;

        const stats = new Map<string, { count: number; last: string | null }>(); // key: customer_id
        for (const order of foodOrders || []) {
          const customerId = (order as any).customer_id;
          if (!customerId) continue;
          const at = (order as any).created_at as string;
          const prev = stats.get(customerId) || { count: 0, last: null };
          const last =
            !prev.last || new Date(at).getTime() > new Date(prev.last).getTime() ? at : prev.last;
          stats.set(customerId, { count: prev.count + 1, last });
        }

        const customersWithStats = (customersData || []).map((customer) => {
          const st = stats.get(customer.id);
          return {
            ...customer,
            totalAppointments: st?.count || 0, // reused field name to avoid breaking UI code
            lastVisit: st?.last || null,
          };
        });

        setCustomers(customersWithStats);
        return;
      }

      // Default: Appointments stats
      setStatLabel("appointments");
      const { data: appointments, error: appointmentsError } = await supabase
        .from("appointments")
        .select("customer_id, start_datetime")
        .eq("business_id", business.id);

      if (appointmentsError) throw appointmentsError;

      const customersWithStats = (customersData || []).map((customer) => {
        const customerAppointments =
          appointments?.filter((a: any) => a.customer_id === customer.id) || [];

        const sortedAppointments = customerAppointments.sort(
          (a: any, b: any) =>
            new Date(b.start_datetime).getTime() - new Date(a.start_datetime).getTime()
        );

        return {
          ...customer,
          totalAppointments: customerAppointments.length,
          lastVisit: sortedAppointments[0]?.start_datetime || null,
        };
      });

      setCustomers(customersWithStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return {
    customers,
    loading,
    error,
    refetch: fetchCustomers,
    statLabel,
  };
}
